<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="700">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input" @submit.prevent="storePlan">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>{{ discount_scheme ? 'Update' : 'New' }} Discount Scheme</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col class="py-0" cols="12">
                            <v-text-field v-model="formData['name']" autofocus :rules="[vRules.required]" density="compact"
                                          label="Name"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['discount_type']" :items="schemeTypes"
                                      :rules="[vRules.required]" density="compact" label="Discount Type"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['discount_value']" :rules="[vRules.required]"
                                          density="compact" label="Discount Value" type="number"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['duration_months']" :rules="[vRules.required]"
                                          density="compact" label="Duration Months" type="number"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['max_total_discount']" density="compact"
                                          label="Max Total Discount" type="number"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['max_total_redemptions']" density="compact"
                                          label="Max Total Redemptions" type="number"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-date-input
                                v-model="formData['valid_from']"
                                :rules="[vRules.required]"
                                append-inner-icon="$calendar"
                                density="compact"
                                hide-actions
                                label="Start Date"
                                prepend-icon=""
                            />
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-date-input
                                v-model="formData['valid_until']"
                                :rules="[vRules.required]"
                                append-inner-icon="$calendar"
                                density="compact"
                                hide-actions
                                label="End Date"
                                prepend-icon=""
                            />
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-checkbox v-model="formData['is_active']" :rules="[vRules.required]" label="Active"/>
                        </v-col>
                        <v-col class="py-0" cols="12">
                            <v-textarea v-model="formData['description']" density="compact" label="Description"/>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {VDateInput} from "vuetify/lib/labs/VDateInput";

const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    discount_scheme: Object
});
const formData = reactive({
    valid: false,
    name: props.discount_scheme?.name ?? null,
    description: props.discount_scheme?.description ?? null,
    discount_type: props.discount_scheme?.discount_type ?? null,
    discount_value: props.discount_scheme?.discount_value ?? null,
    duration_months: props.discount_scheme?.duration_months ?? null,
    max_total_discount: props.discount_scheme?.max_total_discount ?? null,
    max_total_redemptions: props.discount_scheme?.max_total_redemptions ?? null,
    valid_from: props.discount_scheme?.valid_from ? new Date(props.discount_scheme.valid_from) : null,
    valid_until: props.discount_scheme?.valid_until ? new Date(props.discount_scheme.valid_until) : null,
    is_active: !!props.discount_scheme?.is_active ?? null,
});
const emits = defineEmits(['update:modelValue', 'addDiscountScheme']);
const dialogSwitch = ref(props.modelValue);
const errors = ref([]);
// Account Credit (3) is deliberately absent. The redemption path throws
// "not yet supported" on it, and because that propagates through createInvoice
// it refuses the WHOLE activation - so a code built on one stops the customer
// buying at all until they remove it. The API now refuses to create one; this
// stops the form offering an option that can only 422.
//
// An existing type-3 scheme still renders correctly in the list, which keeps its
// own complete label map for exactly that reason.
const schemeTypes = [
    {value: 1, title: 'Percent Off'},
    {value: 2, title: 'Fixed Monthly'},
    {value: 4, title: 'Free Months'},
    {value: 5, title: 'Fixed One Time'},
];

const storePlan = () => {
    errors.value = [];
    const method = props.discount_scheme ? 'put' : 'post';
    const endpoint = props.discount_scheme ? `admin/discount-schemes/${props.discount_scheme.id}` : `admin/discount-schemes`;
    send(method, endpoint, formData)
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                if (props.discount_scheme) {
                    emits('updateDiscountScheme', response.data.data);
                } else {
                    emits('addDiscountScheme', response.data.data);
                }
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}

</script>
